import { createContext, useContext, useReducer, useEffect } from 'react'
import { User, AuthResponse, LoginRequest, RegisterRequest } from '@/types'
import { authApi } from '@/services/api'
import toast from 'react-hot-toast'

interface AuthState {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
}

type AuthAction =
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: User }
  | { type: 'AUTH_FAILURE'; payload: string }
  | { type: 'LOGOUT' }
  | { type: 'CLEAR_ERROR' }

interface AuthContextType extends AuthState {
  login: (credentials: LoginRequest) => Promise<void>
  register: (userData: RegisterRequest) => Promise<void>
  logout: () => Promise<void>
  clearError: () => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  console.log(`🔄 [AUTH_REDUCER] ${action.type}`, {
    timestamp: new Date().toISOString(),
    action,
    currentState: {
      isAuthenticated: state.isAuthenticated,
      isLoading: state.isLoading,
      hasUser: !!state.user,
      userId: state.user?.id,
      userEmail: state.user?.email,
      userRole: state.user?.role,
      hasError: !!state.error
    }
  })

  switch (action.type) {
    case 'AUTH_START':
      const startState = {
        ...state,
        isLoading: true,
        error: null,
      }
      console.log('🔄 [AUTH_REDUCER] AUTH_START result:', {
        timestamp: new Date().toISOString(),
        newState: { isLoading: startState.isLoading, error: startState.error }
      })
      return startState

    case 'AUTH_SUCCESS':
      const successState = {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      }
      console.log('✅ [AUTH_REDUCER] AUTH_SUCCESS result:', {
        timestamp: new Date().toISOString(),
        user: {
          id: action.payload.id,
          email: action.payload.email,
          role: action.payload.role,
          firstName: action.payload.firstName,
          lastName: action.payload.lastName
        },
        newState: {
          isAuthenticated: successState.isAuthenticated,
          isLoading: successState.isLoading
        }
      })
      return successState

    case 'AUTH_FAILURE':
      const failureState = {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
      }
      console.error('❌ [AUTH_REDUCER] AUTH_FAILURE result:', {
        timestamp: new Date().toISOString(),
        error: action.payload,
        newState: {
          isAuthenticated: failureState.isAuthenticated,
          isLoading: failureState.isLoading,
          hasUser: !!failureState.user
        }
      })
      return failureState

    case 'LOGOUT':
      const logoutState = {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      }
      console.log('🚪 [AUTH_REDUCER] LOGOUT result:', {
        timestamp: new Date().toISOString(),
        newState: {
          isAuthenticated: logoutState.isAuthenticated,
          hasUser: !!logoutState.user
        }
      })
      return logoutState

    case 'CLEAR_ERROR':
      const clearErrorState = {
        ...state,
        error: null,
      }
      console.log('🧹 [AUTH_REDUCER] CLEAR_ERROR result:', {
        timestamp: new Date().toISOString(),
        previousError: state.error,
        newError: clearErrorState.error
      })
      return clearErrorState

    default:
      console.warn('⚠️ [AUTH_REDUCER] Unknown action type:', (action as any).type)
      return state
  }
}

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState)

  const login = async (credentials: LoginRequest): Promise<void> => {
    console.log('🔑 [AUTH_CONTEXT] Login attempt started', {
      timestamp: new Date().toISOString(),
      email: credentials.email,
      passwordLength: credentials.password.length
    })

    try {
      dispatch({ type: 'AUTH_START' })

      console.log('🔑 [AUTH_CONTEXT] Calling login API...', {
        timestamp: new Date().toISOString()
      })

      const response = await authApi.login(credentials)
      const { user, accessToken }: AuthResponse = response.data

      console.log('🔑 [AUTH_CONTEXT] Login API successful, storing token...', {
        timestamp: new Date().toISOString(),
        user: {
          id: user.id,
          email: user.email,
          role: user.role,
          firstName: user.firstName,
          lastName: user.lastName
        },
        tokenLength: accessToken.length
      })

      localStorage.setItem('accessToken', accessToken)
      dispatch({ type: 'AUTH_SUCCESS', payload: user })

      console.log('✅ [AUTH_CONTEXT] Login completed successfully', {
        timestamp: new Date().toISOString()
      })
      toast.success('Login successful!')
    } catch (error: any) {
      let errorMessage = 'Login failed. Please try again.'

      // Handle specific error cases with helpful messages
      if (error.response?.status === 401) {
        errorMessage = 'Invalid email or password. Please check your credentials and try again.'
      } else if (error.response?.status === 404) {
        errorMessage = 'Account not found. Please register if you are a new user or check your email address.'
      } else if (error.response?.status === 423) {
        errorMessage = 'Account is locked. Please reset your password or contact support.'
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error
      }

      console.error('❌ [AUTH_CONTEXT] Login failed', {
        timestamp: new Date().toISOString(),
        error: errorMessage,
        status: error.response?.status,
        fullError: error
      })

      dispatch({ type: 'AUTH_FAILURE', payload: errorMessage })

      // Don't throw the error - let the component handle the error state
      // The error will be displayed via the error state in the UI
    }
  }

  const register = async (userData: RegisterRequest): Promise<void> => {
    try {
      dispatch({ type: 'AUTH_START' })

      const response = await authApi.register(userData)
      const { user, accessToken }: AuthResponse = response.data

      localStorage.setItem('accessToken', accessToken)
      dispatch({ type: 'AUTH_SUCCESS', payload: user })

      toast.success('Registration successful!')
    } catch (error: any) {
      let errorMessage = 'Registration failed. Please try again.'

      // Handle specific registration error cases
      if (error.response?.status === 409) {
        errorMessage = 'An account with this email already exists. Please login or use a different email address.'
      } else if (error.response?.status === 400) {
        errorMessage = error.response?.data?.error || 'Invalid registration data. Please check your information and try again.'
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error
      }

      dispatch({ type: 'AUTH_FAILURE', payload: errorMessage })

      // Don't throw the error - let the component handle the error state
    }
  }

  const logout = async (): Promise<void> => {
    try {
      await authApi.logout()
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      localStorage.removeItem('accessToken')
      dispatch({ type: 'LOGOUT' })
      toast.success('Logged out successfully')
    }
  }

  const clearError = (): void => {
    dispatch({ type: 'CLEAR_ERROR' })
  }

  const checkAuthStatus = async (): Promise<void> => {
    console.log('🔍 [AUTH_CONTEXT] Checking authentication status...', {
      timestamp: new Date().toISOString()
    })

    const token = localStorage.getItem('accessToken')

    if (!token) {
      console.log('🔍 [AUTH_CONTEXT] No token found, logging out', {
        timestamp: new Date().toISOString()
      })
      dispatch({ type: 'LOGOUT' })
      return
    }

    console.log('🔍 [AUTH_CONTEXT] Token found, validating with server...', {
      timestamp: new Date().toISOString(),
      tokenLength: token.length
    })

    try {
      const response = await authApi.getCurrentUser()
      console.log('✅ [AUTH_CONTEXT] Token validation successful', {
        timestamp: new Date().toISOString(),
        user: {
          id: response.data.user.id,
          email: response.data.user.email,
          role: response.data.user.role
        }
      })
      dispatch({ type: 'AUTH_SUCCESS', payload: response.data.user })
    } catch (error) {
      console.error('❌ [AUTH_CONTEXT] Token validation failed, logging out', {
        timestamp: new Date().toISOString(),
        error
      })
      localStorage.removeItem('accessToken')
      dispatch({ type: 'LOGOUT' })
    }
  }

  useEffect(() => {
    checkAuthStatus()
  }, [])

  const value: AuthContextType = {
    ...state,
    login,
    register,
    logout,
    clearError,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export default AuthContext